import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type OwnerType = "agency" | "client" | "prospect";
type OrgType = "department" | "job-title" | "location" | "employee-class";

interface OrganizationSelectProps {
  orgType: OrgType;
  ownerType: OwnerType;
  ownerId: string | number;
  value?: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function OrganizationSelect({
  orgType,
  ownerType,
  ownerId,
  value,
  onChange,
  placeholder,
  disabled,
}: OrganizationSelectProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newOfficeName, setNewOfficeName] = React.useState("");
  const [newCity, setNewCity] = React.useState("");
  const [newState, setNewState] = React.useState("");

  // Map orgType to the correct API endpoint
  const getApiEndpoint = (type: OrgType): string => {
    const endpoints: Record<OrgType, string> = {
      "department": "/api/org/departments",
      "job-title": "/api/org/job-titles",
      "location": "/api/org/locations",
      "employee-class": "/api/org/employee-classes",
    };
    return endpoints[type];
  };

  const apiEndpoint = getApiEndpoint(orgType);
  const queryKey = [apiEndpoint, ownerType, ownerId];

  const ownerPayload =
    ownerType === "client"
      ? { ownerType: "client", clientId: ownerId }
      : ownerType === "prospect"
      ? { ownerType: "prospect", prospectId: ownerId }
      : { ownerType: "agency", agencyId: String(ownerId) };

  const { data: items = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await fetch(`${apiEndpoint}?ownerType=${ownerType}&ownerId=${ownerId}`);
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
  });

  const addMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, ...payload }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to add");
      }
      return response.json();
    },
    onSuccess: (newItem) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Success",
        description: `${getLabelForOrgType(orgType)} added successfully`,
        variant: "success",
      });
      onChange(newItem.id);
      setIsAddDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || `Failed to add ${getLabelForOrgType(orgType)}`,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setNewName("");
    setNewOfficeName("");
    setNewCity("");
    setNewState("");
  };

  const handleAdd = () => {
    if (orgType === "location") {
      if (!newOfficeName.trim() || !newCity.trim() || !newState.trim()) return;
      addMutation.mutate({
        officeName: newOfficeName.trim(),
        city: newCity.trim(),
        state: newState.trim(),
      });
    } else {
      if (!newName.trim()) return;
      addMutation.mutate({ name: newName.trim() });
    }
  };

  const getDisplayValue = (item: any) => {
    if (orgType === "location") {
      return [item.officeName, item.city, item.state].filter(Boolean).join(" – ");
    }
    return item.name;
  };

  const getLabelForOrgType = (type: OrgType) => {
    const labels: Record<OrgType, string> = {
      "department": "Department",
      "job-title": "Job Title",
      "location": "Location",
      "employee-class": "Employee Class",
    };
    return labels[type];
  };

  return (
    <>
      <div className="flex gap-2">
        <Select
          value={value?.toString() || undefined}
          onValueChange={(val) => onChange(val ? parseInt(val) : null)}
          disabled={disabled || isLoading}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={placeholder || `Select ${getLabelForOrgType(orgType)}`} />
          </SelectTrigger>
          <SelectContent>
            {items.map((item: any) => (
              <SelectItem key={item.id} value={item.id.toString()}>
                {getDisplayValue(item)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setIsAddDialogOpen(true)}
          disabled={disabled}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {getLabelForOrgType(orgType)}</DialogTitle>
          </DialogHeader>
          {orgType === "location" ? (
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium">Office Name</label>
                <Input
                  value={newOfficeName}
                  onChange={(e) => setNewOfficeName(e.target.value)}
                  placeholder="e.g., Headquarters"
                />
              </div>
              <div>
                <label className="text-sm font-medium">City</label>
                <Input
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  placeholder="e.g., New York"
                />
              </div>
              <div>
                <label className="text-sm font-medium">State</label>
                <Input
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  placeholder="e.g., NY"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={`Enter ${getLabelForOrgType(orgType)} name`}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={addMutation.isPending || (orgType === "location"
                ? !newOfficeName.trim() || !newCity.trim() || !newState.trim()
                : !newName.trim())}
            >
              {addMutation.isPending ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
